import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate } from "@/router";
import MultiStepForm from "@/components/MultiStepForm";
import { DetailsStep } from "../videoDetails";
import {
  GET_MEMBERSHIP_VIDEO_QUERY,
  GET_MEMBERSHIP_VIDEO_WITH_SCORM_QUERY,
  UPDATE_MEMBERSHIP_VIDEO_MUTATION
} from "./queries";
import MultiStepFormSkeleton from "@/components/Skeleton/MultiStepFormSkeleton";
import { LinkProps } from "@generouted/react-router/client";
import { Params, Path } from "@/router";
import { useEffect } from "react";
import AssessmentDetails from "../assessmentDetails";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { prepareAssessmentForSubmit } from "../_utils";
import { ShowVideoPage } from "./show";
import Tag from "@/components/Tag";
import Banner from "@/components/Banner";
import ObjectHeading from "@/components/ObjectHeading";
import { useReturnTo } from "@/hooks/useSearchParams";
import PageTitle from "@/components/PageTitle";
import { useFlags } from "@/hooks/useFeatureFlags";

export default function EditVideoPage() {
  const location = useLocation();
  const videoFromState = location.state?.video;

  const videoId = location.pathname?.split("/").pop();

  const flags = useFlags();
  const scormFlagEnabled = !!flags["backoffice-scorm-courses"];

  // Flag-off selects identity fields only; flag-on adds status/launch fields.
  const { data, loading } = useQuery(
    scormFlagEnabled ? GET_MEMBERSHIP_VIDEO_WITH_SCORM_QUERY : GET_MEMBERSHIP_VIDEO_QUERY
  );

  const fetchedVideo = data?.membershipVideos?.nodes?.find((v: any) => v.id === videoId);

  const video = videoFromState || fetchedVideo;

  const navigate = useNavigate();

  const returnTo = useReturnTo();
  const homePath: LinkProps<Path, Params> = { to: "/education/videos" };
  const backLink = {
    to: returnTo ? returnTo : homePath.to,
    title: "Cancel"
  } as unknown as LinkProps<Path, Params>;

  const initialValues = {
    id: video?.id,
    title: video?.title,
    description: video?.description || "",
    url: video?.url,
    series: video?.series,
    creditCategory: video?.creditCategory.toLowerCase(),
    publishedAt: {
      startDate: new Date(video?.publishedAt),
      endDate: new Date(video?.publishedAt)
    },
    expiredAt: {
      startDate: new Date(video?.expiredAt),
      endDate: new Date(video?.expiredAt)
    },
    durationInSeconds: video?.durationInSeconds,
    contentType: (video?.contentType as "vimeo" | "scorm") || "vimeo",
    creditAmount: video?.creditAmount ?? 1.0,
    scormPackage: null as File | null,
    assessment: {
      id: video?.assessment?.id,
      questions:
        video?.assessment?.questions?.map((question: any) => ({
          id: question.id,
          content: question.content,
          position: question.position,
          options: question.options.map((option: any) => ({
            id: option.id,
            content: option.content,
            answer: option.answer,
            position: option.position
          }))
        })) ?? []
    }
  };

  const form = useForm({
    defaultValues: initialValues
  });

  const [updateMembershipVideo] = useMutation(UPDATE_MEMBERSHIP_VIDEO_MUTATION);

  useEffect(() => {
    if (!video && !loading) {
      navigate(homePath.to);
    }
  }, [video, loading, homePath.to, navigate]);

  const isScorm = (form.watch("contentType") || "vimeo") === "scorm";

  async function handleSubmit(formData: any) {
    const isValid = await form.trigger();
    if (isValid) {
      try {
        // Omit SCORM-only fields from the legacy Vimeo payload when the flag is off.
        const { scormPackage, assessment, url, contentType, creditAmount, ...rest } = formData;
        const input: Record<string, unknown> = {
          id: video.id,
          ...rest,
          publishedAt: formData.publishedAt.startDate,
          expiredAt: formData.expiredAt?.startDate
        };

        if (scormFlagEnabled) {
          input.contentType = contentType;
          input.creditAmount = Number(creditAmount);

          if (contentType === "scorm") {
            if (scormPackage instanceof File) {
              input.scormPackage = scormPackage;
            }
          } else {
            input.url = url;
            input.assessment = prepareAssessmentForSubmit(assessment);
          }
        } else {
          input.url = url;
          input.assessment = prepareAssessmentForSubmit(assessment);
        }

        const response = await updateMembershipVideo({ variables: { input } });

        if (response.data.updateMembershipVideo.success) {
          navigate(homePath.to);
          showSnackbar("Video Updated Successfully");
        } else {
          showSnackbar(response.data.updateMembershipVideo.message);
        }
      } catch (e) {
        showSnackbar("Error updating video");
      }
    }
  }

  const { hasPermission } = useCapabilities();
  const now = new Date();
  const expiredAt = form.watch("expiredAt").startDate;
  const scormDisabledByFlag = isScorm && !scormFlagEnabled;
  const readOnly = !hasPermission(PERMISSION.manageVideos) || (expiredAt && now >= expiredAt) || scormDisabledByFlag;

  const tag =
    form.watch("expiredAt") && form.watch("expiredAt").startDate < now ? (
      <Tag tagType="surface" tagStyle="strong">
        Expired
      </Tag>
    ) : form.watch("publishedAt") && form.watch("publishedAt").startDate > now ? (
      <Tag tagType="information" tagStyle="strong">
        Upcoming
      </Tag>
    ) : (
      <Tag tagType="success" tagStyle="strong">
        Active
      </Tag>
    );

  const steps = isScorm
    ? [
        {
          headline: "Details",
          supportingText: "Step 1",
          content: <DetailsStep form={form} isEditing />
        }
      ]
    : [
        {
          headline: "Details",
          supportingText: "Step 1",
          content: <DetailsStep form={form} isEditing />
        },
        {
          headline: "Assessment",
          supportingText: "Step 2",
          content: <AssessmentDetails form={form} />
        }
      ];

  if (loading) {
    return <MultiStepFormSkeleton stepCount={2} renderStepper={true} fullWidth={true} showObjectHeading={true} />;
  }

  const scormStatus = video?.scormStatus as "processing" | "ready" | "failed" | undefined;
  const scormStatusBanner =
    isScorm && scormStatus === "processing" ? (
      <div className="mb-4" data-testid="scorm-status-banner">
        <Banner variant="info" description="Re-extracting package…" />
      </div>
    ) : isScorm && scormStatus === "failed" ? (
      <div className="mb-4" data-testid="scorm-status-banner">
        <Banner variant="error" description={video?.scormStatusMessage || "SCORM package failed to extract."} />
      </div>
    ) : null;

  const scormDisabledBanner = scormDisabledByFlag ? (
    <div className="mb-4" data-testid="scorm-disabled-banner">
      <Banner
        variant="warn"
        description="SCORM courses are currently disabled. Contact #help-tech-support if you need to make changes."
      />
    </div>
  ) : null;

  if (readOnly) {
    return (
      <ShowVideoPage
        form={form}
        topBanner={
          <>
            {scormDisabledBanner}
            {scormStatusBanner}
          </>
        }
      />
    );
  }

  return (
    <>
      <PageTitle
        pattern="/education/videos/:videoSlug"
        title={`Education | Videos | Video Details | ${form.watch("title")}`}
      />
      {scormStatusBanner}
      <MultiStepForm
        form={form}
        steps={steps}
        renderStepper={true}
        onSubmit={handleSubmit}
        backLink={backLink}
        modelName="Video"
        submitText="Publish Changes"
        isEditing={true}
        enableSubmitOnLastStep={true}
        fullWidth={true}
        objectHeading={
          <ObjectHeading
            title={video?.title}
            metaData={[{ text: String(form.watch("creditAmount") ?? 1), label: "Credits" }]}
            iconName="assignment"
            tag={tag}
          />
        }
      />
    </>
  );
}
